import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import CookieConsent from "@/components/cookie-consent";
import { AuthProvider } from "@/components/auth-provider";
import { QueryProvider } from "@/providers/providers";

// Layouts
import MarketingLayout from "@/layouts/MarketingLayout";
import LandlordLayout from "@/layouts/LandlordLayout";
import TenantLayout from "@/layouts/TenantLayout";
import AdminLayout from "@/layouts/AdminLayout";

// Marketing pages
import HomePage from "@/pages/marketing/HomePage";
import AboutPage from "@/pages/marketing/AboutPage";
import ContactPage from "@/pages/marketing/ContactPage";
import PortfolioPage from "@/pages/marketing/PortfolioPage";
import PropertiesPage from "@/pages/marketing/PropertiesPage";
import PropertyDetailPage from "@/pages/marketing/PropertyDetailPage";
import FavoritesPage from "@/pages/marketing/FavoritesPage";
import ServicesPage from "@/pages/marketing/ServicesPage";
import LandlordPage from "@/pages/marketing/LandlordPage";

// Auth pages
import SignInPage from "@/pages/auth/SignInPage";
import SignUpPage from "@/pages/auth/SignUpPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import AcceptInvitePage from "@/pages/auth/AcceptInvitePage";

// Landlord pages
import LandlordDashboard from "@/pages/landlord/LandlordDashboard";
import LandlordApplications from "@/pages/landlord/ApplicationsPage";
import LandlordLeases from "@/pages/landlord/LeasesPage";
import LeaseDetailPage from "@/pages/landlord/LeaseDetailPage";
import LandlordMaintenance from "@/pages/landlord/MaintenancePage";
import LandlordInquiries from "@/pages/landlord/InquiriesPage";
import LandlordProperties from "@/pages/landlord/PropertiesPage";
import LandlordTenants from "@/pages/landlord/TenantsPage";
import PropertyAddWizard from "@/pages/landlord/PropertyAddWizard";
import PropertyDetailsPage from "@/pages/landlord/PropertyDetailsPage";
import PropertyEditPage from "@/pages/landlord/PropertyEditPage";

// Tenant pages
import TenantDashboard from "@/pages/tenant/TenantDashboard";

// Admin pages
import AdminProperties from "@/pages/admin/PropertiesPage";
import AdminUsers from "@/pages/admin/UsersPage";
import AdminLeases from "@/pages/admin/LeasesPage";
import AdminSettings from "@/pages/admin/SettingsPage";
import AdminApprovals from "@/pages/admin/ApprovalsPage";

function App() {
  return (
    <QueryProvider>
      <AuthProvider>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <div className="flex min-h-screen flex-col">
            <Routes>
              {/* Marketing routes */}
              <Route element={<MarketingLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/portfolio" element={<PortfolioPage />} />
                <Route path="/properties" element={<PropertiesPage />} />
                <Route
                  path="/properties/:id"
                  element={<PropertyDetailPage />}
                />
                <Route
                  path="/properties/favorites"
                  element={<FavoritesPage />}
                />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/landlord" element={<LandlordPage />} />
              </Route>

              {/* Auth routes */}
              <Route path="/auth/signin" element={<SignInPage />} />
              <Route path="/auth/signup" element={<SignUpPage />} />
              <Route
                path="/auth/accept-invite"
                element={<AcceptInvitePage />}
              />
              <Route
                path="/auth/forgot-password"
                element={<ForgotPasswordPage />}
              />

              {/* Landlord Dashboard routes */}
              <Route path="/landlord/dashboard" element={<LandlordLayout />}>
                <Route index element={<LandlordDashboard />} />
              </Route>
              <Route path="/landlord/applications" element={<LandlordLayout />}>
                <Route index element={<LandlordApplications />} />
              </Route>
              <Route path="/landlord/leases" element={<LandlordLayout />}>
                <Route index element={<LandlordLeases />} />
                <Route path=":id" element={<LeaseDetailPage />} />
              </Route>
              <Route path="/landlord/maintenance" element={<LandlordLayout />}>
                <Route index element={<LandlordMaintenance />} />
              </Route>
              <Route path="/landlord/inquiries" element={<LandlordLayout />}>
                <Route index element={<LandlordInquiries />} />
              </Route>
              <Route path="/landlord/tenants" element={<LandlordLayout />}>
                <Route index element={<LandlordProperties />} />
              </Route>
              <Route
                path="/landlord/properties/add"
                element={<LandlordLayout />}
              >
                <Route index element={<PropertyAddWizard />} />
              </Route>
              <Route
                path="/landlord/properties/:id"
                element={<LandlordLayout />}
              >
                <Route index element={<PropertyDetailsPage />} />
              </Route>
              <Route
                path="/landlord/properties/:id/edit"
                element={<LandlordLayout />}
              >
                <Route index element={<PropertyEditPage />} />
              </Route>
              <Route path="/landlord/tenants" element={<LandlordLayout />}>
                <Route index element={<LandlordTenants />} />
              </Route>

              {/* Tenant routes */}
              <Route path="/tenant/dashboard" element={<TenantLayout />}>
                <Route index element={<TenantDashboard />} />
              </Route>

              {/* Admin routes */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route
                  index
                  element={<Navigate to="/admin/properties" replace />}
                />
                <Route path="properties" element={<AdminProperties />} />
                <Route path="approvals" element={<AdminApprovals />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="leases" element={<AdminLeases />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="approvals" element={<AdminApprovals />} />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          <Toaster />
          <CookieConsent />
        </ThemeProvider>
      </AuthProvider>
    </QueryProvider>
  );
}

export default App;
